import {
  BankOutlined,
  CalendarOutlined,
  DeleteOutlined,
  EditOutlined,
  GlobalOutlined,
  PlusOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  message,
  Popconfirm,
  Row,
  Space,
  Statistic,
  Table,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { useAuthStore } from '../store/authStore';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  logo?: string;
  accountHolder?: string;
  accountHolderAddress?: string;
  accountHolderRegistryNumber?: string;
  accountHolderTaxId?: string;
  accountNumber?: string;
  bankName?: string;
  paymentCode?: string;
  paymentPurpose?: string;
  currency?: string;
  accountantEmail?: string;
  createdAt: string;
  _count?: {
    users: number;
    events: number;
  };
}

const Organizations: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [form] = Form.useForm();
  const { user } = useAuthStore();
  const { t } = useTranslation();

  useEffect(() => {
    if (user?.role === 'SUPER_ADMIN') {
      fetchOrganizations();
    }
  }, [user?.role]);

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const response = await api.get('/organizations');
      setOrganizations(response.data || []);
    } catch (error) {
      message.error(t('organizations.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingOrg(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: Organization) => {
    setEditingOrg(record);
    form.setFieldsValue({
      name: record.name,
      slug: record.slug,
      description: record.description,
      logo: record.logo,
      accountHolder: record.accountHolder,
      accountHolderAddress: record.accountHolderAddress,
      accountHolderRegistryNumber: record.accountHolderRegistryNumber,
      accountHolderTaxId: record.accountHolderTaxId,
      accountNumber: record.accountNumber,
      bankName: record.bankName,
      paymentCode: record.paymentCode,
      paymentPurpose: record.paymentPurpose,
      currency: record.currency,
      accountantEmail: record.accountantEmail,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/organizations/${id}`);
      message.success(t('organizations.deleted'));
      fetchOrganizations();
    } catch (error) {
      message.error(t('messages.delete_error'));
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        name: values.name,
        slug: values.slug,
        description: values.description,
        logo: values.logo,
        accountHolder: values.accountHolder,
        accountHolderAddress: values.accountHolderAddress,
        accountHolderRegistryNumber: values.accountHolderRegistryNumber,
        accountHolderTaxId: values.accountHolderTaxId,
        accountNumber: values.accountNumber,
        bankName: values.bankName,
        paymentCode: values.paymentCode,
        paymentPurpose: values.paymentPurpose,
        currency: values.currency,
        accountantEmail: values.accountantEmail,
      };

      if (editingOrg) {
        await api.patch(`/organizations/${editingOrg.id}`, data);
        message.success(t('organizations.updated'));
      } else {
        await api.post('/organizations', data);
        message.success(t('organizations.created'));
      }

      setModalVisible(false);
      fetchOrganizations();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('organizations.operationFailed'));
    }
  };

  // Only SUPER_ADMIN should see this page
  if (user?.role !== 'SUPER_ADMIN') {
    return (
      <Card>
        <Title level={3}>{t('organizations.accessDeniedTitle')}</Title>
        <Text>{t('organizations.accessDeniedBody')}</Text>
      </Card>
    );
  }

  const columns: ColumnsType<Organization> = [
    {
      title: t('organizations.columnOrganization'),
      key: 'name',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Space>
            <BankOutlined />
            <strong>{record.name}</strong>
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            /{record.slug}
          </Text>
        </Space>
      ),
    },
    {
      title: t('organizations.columnUsers'),
      key: 'users',
      align: 'center',
      render: (_, record) => (
        <Space>
          <UserOutlined />
          <span>{record._count?.users || 0}</span>
        </Space>
      ),
    },
    {
      title: t('organizations.columnEvents'),
      key: 'events',
      align: 'center',
      render: (_, record) => (
        <Space>
          <CalendarOutlined />
          <span>{record._count?.events || 0}</span>
        </Space>
      ),
    },
    {
      title: t('organizations.columnCreatedAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format('YYYY.MM.DD'),
    },
    {
      title: t('common.actions'),
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title={t('common.confirm_delete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>{t('organizations.title')}</Title>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {t('organizations.newOrganization')}
          </Button>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('organizations.statTotalOrganizations')}
              value={organizations.length}
              prefix={<BankOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('organizations.statTotalUsers')}
              value={organizations.reduce((sum, o) => sum + (o._count?.users || 0), 0)}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('organizations.statTotalEvents')}
              value={organizations.reduce((sum, o) => sum + (o._count?.events || 0), 0)}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={organizations}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => t('organizations.paginationTotal', { count: total }),
          }}
        />
      </Card>

      <Modal
        title={editingOrg ? t('organizations.editTitle') : t('organizations.createTitle')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={900}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label={t('organizationForm.nameLabel')}
                rules={[{ required: true, message: t('common.requiredField') }]}
              >
                <Input
                  prefix={<BankOutlined />}
                  placeholder={t('organizationForm.namePlaceholder')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="slug"
                label={t('organizationForm.slugLabel')}
                rules={[
                  { required: true, message: t('common.requiredField') },
                  {
                    pattern: /^[a-z0-9-]+$/,
                    message: t('organizationForm.slugPattern'),
                  },
                ]}
              >
                <Input placeholder={t('organizationForm.slugPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label={t('common.description')}>
            <TextArea rows={3} placeholder={t('organizationForm.descriptionPlaceholder')} />
          </Form.Item>

          <Form.Item name="logo" label={t('organizations.logoUrlLabel')}>
            <Input
              prefix={<GlobalOutlined />}
              placeholder={t('organizations.logoUrlPlaceholder')}
            />
          </Form.Item>

          <Title level={5} style={{ marginTop: 24 }}>
            {t('organizationForm.bankDivider')}
          </Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {t('organizationForm.bankHint')}
          </Text>

          <Form.Item
            name="accountHolder"
            label={t('organizationForm.accountHolderLabel')}
            help={t('organizationForm.accountHolderHelp')}
            rules={[{ max: 70, message: t('organizationForm.accountHolderMax') }]}
          >
            <Input
              maxLength={70}
              showCount
              placeholder={t('organizationForm.accountHolderPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            name="accountHolderAddress"
            label={t('organizationForm.addressLabel')}
            help={t('organizationForm.addressHelp')}
            rules={[{ max: 200, message: t('organizationForm.addressMax') }]}
          >
            <TextArea
              rows={2}
              maxLength={200}
              showCount
              placeholder={t('organizationForm.addressPlaceholder')}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="accountHolderRegistryNumber"
                label={t('organizationForm.registryNumberLabel')}
                rules={[{ max: 50, message: t('organizationForm.registryNumberMax') }]}
              >
                <Input
                  maxLength={50}
                  placeholder={t('organizationForm.registryNumberPlaceholder')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="accountHolderTaxId"
                label={t('organizationForm.taxIdLabel')}
                rules={[{ max: 50, message: t('organizationForm.taxIdMax') }]}
              >
                <Input maxLength={50} placeholder={t('organizationForm.taxIdPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="accountNumber"
                label={t('organizationForm.accountNumberLabel')}
                help={t('organizationForm.accountNumberHelp')}
                rules={[
                  {
                    pattern: /^[\d\s-]{18,22}$/,
                    message: t('organizationForm.accountNumberPattern'),
                  },
                ]}
              >
                <Input maxLength={22} placeholder="000-0000000000000-00" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="currency"
                label={t('organizationForm.currencyLabel')}
                help={t('organizationForm.currencyHelp')}
                rules={[
                  {
                    pattern: /^[A-Z]{3}$/,
                    message: t('organizationForm.currencyPattern'),
                  },
                ]}
                normalize={(v: string | undefined) => v?.toUpperCase()}
              >
                <Input maxLength={3} placeholder={t('organizationForm.currencyPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="bankName"
                label={t('organizationForm.bankNameLabel')}
                rules={[{ max: 70, message: t('organizationForm.bankNameMax') }]}
              >
                <Input maxLength={70} placeholder={t('organizationForm.bankNamePlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="paymentCode"
                label={t('organizationForm.paymentCodeLabel')}
                help={t('organizationForm.paymentCodeHelp')}
                rules={[
                  {
                    pattern: /^\d{3}$/,
                    message: t('organizationForm.paymentCodePattern'),
                  },
                ]}
              >
                <Input maxLength={3} placeholder={t('organizationForm.paymentCodePlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="paymentPurpose"
            label={t('organizationForm.paymentPurposeLabel')}
            help={t('organizationForm.paymentPurposeHelp')}
            rules={[
              {
                max: 35,
                message: t('organizationForm.paymentPurposeMax'),
              },
            ]}
          >
            <TextArea
              rows={2}
              maxLength={35}
              showCount
              placeholder={t('organizationForm.paymentPurposePlaceholder')}
            />
          </Form.Item>

          <Title level={5} style={{ marginTop: 24 }}>
            {t('organizations.accountantTitle')}
          </Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {t('organizations.accountantHint')}
          </Text>
          <Form.Item
            name="accountantEmail"
            label={t('organizations.accountantEmailLabel')}
            rules={[{ type: 'email', message: t('organizations.accountantEmailInvalid') }]}
          >
            <Input placeholder={t('organizations.accountantEmailPlaceholder')} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingOrg ? t('organizations.save') : t('common.create')}
              </Button>
              <Button onClick={() => setModalVisible(false)}>{t('organizations.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Organizations;
